import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import './FloatingNavigation.css';
import {
  FaBars,
  FaTimes,
  FaTachometerAlt,
  FaChartBar,
  FaUserCog,
  FaMapMarkerAlt,
  FaStickyNote,
  FaChartLine,
  FaUserFriends,
  FaPlus,
  FaKey,
  FaSignOutAlt
} from 'react-icons/fa';

const FloatingNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  // Navigation items grouped by sections
  const navigationSections = [
    {
      title: 'Dashboard',
      items: [
        { title: 'Admin Dashboard', icon: <FaTachometerAlt />, path: '/admin-dashboard' },
        { title: 'Sales Overview', icon: <FaChartBar />, path: '/admin-sales-overview' }
      ]
    },
    {
      title: 'Management',
      items: [
        { title: 'User Management', icon: <FaUserCog />, path: '/manage-users' },
        { title: 'Site Management', icon: <FaMapMarkerAlt />, path: '/manage-sites' },
        { title: 'Notes Management', icon: <FaStickyNote />, path: '/manage-notes' }
      ]
    },
    {
      title: 'Guides & Analytics',
      items: [
        { title: 'Guide Analytics', icon: <FaChartLine />, path: '/guide-analytics' },
        { title: 'View Guides', icon: <FaUserFriends />, path: '/view-guides' },
        { title: 'Add Guide', icon: <FaPlus />, path: '/add-guide' }
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Floating Menu Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            floating-button w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg
            flex items-center justify-center transition-all duration-300 opacity-75 hover:opacity-100
            backdrop-blur ${isOpen ? 'rotate-180' : 'rotate-0'}
          `}
        >
          {isOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
        </button>
      </div>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Navigation Modal */}
      <div className={`
        fixed bottom-24 right-6 w-80 bg-white rounded-xl border border-slate-200 z-50
        floating-modal-shadow transform transition-all duration-300 origin-bottom-right
        ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}
      `}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-base">
              {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-base font-semibold text-slate-800">{user?.name || user?.username || 'User'}</p>
              <p className="text-xs text-slate-500">{user?.role || 'User'}</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-slate-200 rounded-md transition-colors"
          >
            <FaTimes className="text-slate-500 text-sm" />
          </button>
        </div>

        {/* Navigation Content */}
        <div className="max-h-96 overflow-y-auto p-2 floating-nav-scroll">
          {navigationSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    onClick={() => handleNavigation(item.path)}
                    className={`
                      w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
                      transition-all duration-200 text-left
                      ${isActive(item.path)
                        ? 'bg-blue-100 text-blue-700 border-l-3 border-blue-500'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                      }
                    `}
                  >
                    <span className={`mr-3 ${isActive(item.path) ? 'text-blue-500' : 'text-slate-400'}`}>
                      {item.icon}
                    </span>
                    {item.title}
                    {isActive(item.path) && (
                      <span className="ml-auto w-2 h-2 bg-blue-500 rounded-full"></span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Modal Footer */}
        <div className="border-t border-slate-200 p-3 bg-slate-50 rounded-b-xl space-y-1">
          <button
            onClick={() => handleNavigation('/change-password')}
            className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
          >
            <FaKey className="mr-3" />
            Change Password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
          >
            <FaSignOutAlt className="mr-3" />
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default FloatingNavigation;
